import { useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import { Input } from '../../../shared/components/Input';
import { Toggle } from '../../../shared/components/Toggle';
import Button from '../../../shared/components/Button';
import { formatMinor } from '../../../shared/utils/money';
import {
  useCancelOrderWithRefund,
  useCharterOrder,
  useConfirmOrder,
  useOrderBalanceLink,
  useScheduleOrderSession,
} from '../hooks/useCharter';
import { BookingStatusBadge, PaymentStatusBadge } from './CharterStatusBadge';
import { DetailField, InspirationPreview, IntakeResponses } from './IntakeResponses';
import { formatWat } from './charterFormat';
import type { CharterOrderSession } from '../types';

interface Props {
  orderId: string;
  tenantId: string | null;
  onClose: () => void;
  /** When false, hides all action controls (TCS-admin read-only oversight — refunds are audit-only). */
  canManage?: boolean;
}

export function OrderDetailModal({ orderId, tenantId, onClose, canManage = true }: Props) {
  const { data: order, isLoading, error } = useCharterOrder(orderId, tenantId);
  const confirm = useConfirmOrder();
  const cancelRefund = useCancelOrderWithRefund();
  const balanceLink = useOrderBalanceLink();
  const scheduleSession = useScheduleOrderSession();

  const [refundMajor, setRefundMajor] = useState('');
  const [credit, setCredit] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [balanceUrl, setBalanceUrl] = useState<string | null>(null);
  // Per-session slot id inputs, keyed by stepId.
  const [sessionSlots, setSessionSlots] = useState<Record<string, string>>({});

  const runBalanceLink = async () => {
    const result = await balanceLink.mutateAsync(orderId);
    setBalanceUrl(result.paymentUrl);
  };

  const runRefundCancel = () => {
    const amountNum = refundMajor.trim() === '' ? undefined : parseFloat(refundMajor);
    cancelRefund.mutate({
      id: orderId,
      data: {
        refundAmount: amountNum !== undefined && Number.isFinite(amountNum) ? Math.round(amountNum * 100) : undefined,
        credit: credit || undefined,
        reason: refundReason || undefined,
      },
    });
  };

  const scheduleOne = (session: CharterOrderSession) => {
    const slotId = sessionSlots[session.stepId]?.trim();
    scheduleSession.mutate({
      id: orderId,
      stepId: session.stepId,
      data: { sessionStepId: session.stepId, slotId: slotId || undefined },
    });
  };

  const hasDeposit = !!order?.deposit && (order.remainingBalance ?? 0) > 0;
  const slotless = order?.requiresSlot === false;

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <div className="p-8 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Order detail</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">
            &times;
          </button>
        </div>

        {isLoading ? (
          <p className="text-muted-foreground py-8 text-center">Loading order…</p>
        ) : error || !order ? (
          <p className="text-red-500 py-8 text-center">Failed to load order.</p>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <DetailField label="Reference">
                <span className="font-mono text-xs">{order.id}</span>
              </DetailField>
              <DetailField label="Status">
                <BookingStatusBadge status={order.status} />
              </DetailField>
              <DetailField label="Payment">
                <PaymentStatusBadge status={order.paymentStatus} />
              </DetailField>
              <DetailField label="Fulfilment">{slotless ? 'Async (slot-less)' : 'Slot-based'}</DetailField>
              <DetailField label="Slot (WAT)">
                {order.slot ? `${formatWat(order.slot.startsAt)} → ${formatWat(order.slot.endsAt)}` : 'Unscheduled'}
              </DetailField>
              <DetailField label="Deposit / balance">
                {order.deposit ? formatMinor(order.deposit.amount, order.deposit.currency) : '—'}
                {order.remainingBalance ? ` / ${formatMinor(order.remainingBalance, order.currency)} due` : ''}
              </DetailField>
            </div>

            {order.customer && (
              <div className="rounded-[12px] border border-border bg-muted p-3">
                <p className="text-sm font-medium text-foreground">{order.customer.name}</p>
                <p className="text-xs text-muted-foreground">
                  {order.customer.email}
                  {order.customer.phone ? ` · ${order.customer.phone}` : ''}
                  {order.customer.timezone ? ` · ${order.customer.timezone}` : ''}
                </p>
              </div>
            )}

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Inspiration (referenced portfolio work)
              </p>
              <InspirationPreview inspiration={order.inspiration} />
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Intake responses
              </p>
              <IntakeResponses formData={order.formData} />
            </div>

            {/* Bundled sessions — for slot-less packages the provider schedules these manually. */}
            {order.sessions && order.sessions.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Bundled sessions
                </p>
                <div className="rounded-[12px] border border-border divide-y divide-border">
                  {order.sessions.map((session) => (
                    <div key={session.stepId} className="p-3 flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-[160px]">
                        <p className="text-sm text-foreground">{session.stepId}</p>
                        <p className="text-xs text-muted-foreground">
                          {session.status}
                          {session.startsAt ? ` · ${formatWat(session.startsAt)}` : ''}
                        </p>
                      </div>
                      {canManage && session.status === 'UNSCHEDULED' && (
                        <div className="flex items-end gap-2">
                          <Input
                            placeholder="slot id (optional)"
                            value={sessionSlots[session.stepId] ?? ''}
                            onChange={(e) =>
                              setSessionSlots((prev) => ({ ...prev, [session.stepId]: e.target.value }))
                            }
                            className="max-w-[200px]"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-[38px]"
                            onClick={() => scheduleOne(session)}
                            disabled={scheduleSession.isPending}
                          >
                            Schedule
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canManage && (
              <div className="border-t border-border pt-5 space-y-5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Actions</p>

                <div className="flex flex-wrap gap-3">
                  <Button
                    size="sm"
                    className="h-[38px]"
                    onClick={() => confirm.mutate(orderId)}
                    disabled={confirm.isPending || order.status === 'CANCELLED'}
                  >
                    Confirm
                  </Button>
                  {hasDeposit && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-[38px]"
                      onClick={runBalanceLink}
                      disabled={balanceLink.isPending}
                      loading={balanceLink.isPending}
                    >
                      Send balance payment link
                    </Button>
                  )}
                </div>

                {balanceUrl && (
                  <div className="rounded-[10px] border border-border bg-muted p-3 text-sm break-all">
                    <span className="text-muted-foreground">Balance link: </span>
                    <a href={balanceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {balanceUrl}
                    </a>
                  </div>
                )}

                {/* Cancel with the merchant's refund decision (partial/full refund or store credit). */}
                <div className="rounded-[12px] border border-border bg-muted p-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">Cancel with refund decision</p>
                  <p className="text-xs text-muted-foreground">
                    The refund decision is yours: refund a partial/full amount, issue store credit, or neither.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Refund amount (₦, optional)"
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0.00"
                      value={refundMajor}
                      onChange={(e) => setRefundMajor(e.target.value)}
                    />
                    <Input
                      label="Reason (optional)"
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                    />
                  </div>
                  <Toggle label="Issue store credit instead of a monetary refund" checked={credit} onChange={setCredit} />
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-[40px] !text-red-600 !border-red-300"
                      onClick={runRefundCancel}
                      disabled={cancelRefund.isPending || order.status === 'CANCELLED'}
                      loading={cancelRefund.isPending}
                    >
                      Cancel order
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
